import { useEffect, useSyncExternalStore } from "react";
import { LocalStorageKeys } from "@/config/localStorageKeys";
import { getCookie, setCookie } from "@/utils/cookies";
import { getFromStorage, setToStorage } from "@/utils/localStorageUtils";

/**
 * Whether the dashboard should stop asking the user to install the Chrome
 * extension (R-2246).
 *
 * The extension's background script writes `webshare_extension_version` on
 * `.webshare.io` when it is installed or updated (chrome-extension,
 * `installationCookieSync.ts`). That write happens outside the page, so the
 * page is never told about it: a dashboard tab open during the install kept
 * showing the banner until a reload. The extension also writes the cookie
 * without an expiry, so a browser restart drops it and the banner came back
 * for everyone who already had the extension.
 *
 * The cookie is therefore read as an external store, re-read whenever the
 * browser reports a cookie change (Cookie Store API) or the tab regains focus,
 * and the version seen is remembered in localStorage so a restart does not
 * forget the extension.
 */
const EXTENSION_VERSION_COOKIE = "webshare_extension_version";
const EXTENSION_DISMISSED_COOKIE = "webshare_extension_dismissed";
const COOKIE_CHANGE_EVENT = "cookieChange";

function readHasExtension() {
  return (
    !!getCookie(EXTENSION_VERSION_COOKIE) ||
    !!getCookie(EXTENSION_DISMISSED_COOKIE) ||
    !!getFromStorage(LocalStorageKeys.ExtensionSeenVersion)
  );
}

function subscribe(onChange: () => void) {
  // Firefox and Safari have no Cookie Store API; the focus and visibility
  // listeners cover coming back from the Web Store tab there.
  const cookieStore = window.cookieStore as CookieStore | undefined;
  cookieStore?.addEventListener("change", onChange);
  window.addEventListener(COOKIE_CHANGE_EVENT, onChange);
  window.addEventListener("focus", onChange);
  document.addEventListener("visibilitychange", onChange);

  return () => {
    cookieStore?.removeEventListener("change", onChange);
    window.removeEventListener(COOKIE_CHANGE_EVENT, onChange);
    window.removeEventListener("focus", onChange);
    document.removeEventListener("visibilitychange", onChange);
  };
}

const getServerSnapshot = () => false;

export default function useHasExtension() {
  const hasExtension = useSyncExternalStore(
    subscribe,
    readHasExtension,
    getServerSnapshot
  );

  useEffect(() => {
    const version = getCookie(EXTENSION_VERSION_COOKIE);
    if (version) {
      setToStorage(LocalStorageKeys.ExtensionSeenVersion, version);
    }
  }, [hasExtension]);

  const dismissExtension = () => {
    setCookie(EXTENSION_DISMISSED_COOKIE, "true");
    window.dispatchEvent(new Event(COOKIE_CHANGE_EVENT));
  };

  return { hasExtension, dismissExtension };
}
